import { isSameDate } from '@/helpers/date';
import {
  AvailabilityResult,
  BookingShop,
  OneFFAvailabilitiesByServiceItemOptionTimeSlot,
  SlotInterface,
  TimeSlotPerDateInterface,
} from '@repo/common/interfaces';
import { DateTime } from 'luxon';

export const adaptOneFFAvailabilitiesByServiceItemOptionTimeSlotToSlotInterface =
  (
    timeSlot: OneFFAvailabilitiesByServiceItemOptionTimeSlot,
    fulfillerId: string,
    optionId: string,
    timeZone: string,
  ): SlotInterface => {
    let dateInUTC: DateTime;
    let endInUTC: DateTime;

    if (
      Intl.DateTimeFormat().resolvedOptions().timeZone.toLocaleLowerCase() !==
      timeZone.toLocaleLowerCase()
    ) {
      dateInUTC = DateTime.fromSeconds(timeSlot?.start_sec, {
        zone: 'utc',
      })
        .toUTC()
        .setZone(timeZone);
      endInUTC = DateTime.fromSeconds(
        timeSlot?.start_sec + timeSlot?.duration_sec,
        { zone: 'utc' },
      )
        .toUTC()
        .setZone(timeZone);
    } else {
      dateInUTC = DateTime.fromSeconds(timeSlot?.start_sec, {
        zone: timeZone,
      });
      endInUTC = DateTime.fromSeconds(
        timeSlot?.start_sec + timeSlot?.duration_sec,
        { zone: timeZone },
      );
    }

    let startDateTime = DateTime.fromSeconds(timeSlot?.start_sec).set({
      day: dateInUTC.day,
      month: dateInUTC.month,
      year: dateInUTC.year,
      hour: dateInUTC.hour,
      minute: dateInUTC.minute,
      second: 0,
      millisecond: 0,
    });

    let endDateTime = DateTime.fromSeconds(
      timeSlot?.start_sec + timeSlot?.duration_sec,
    ).set({
      day: endInUTC.day,
      month: endInUTC.month,
      year: endInUTC.year,
      hour: endInUTC.hour,
      minute: endInUTC.minute,
      second: 0,
      millisecond: 0,
    });

    return {
      id: timeSlot.start_sec?.toString(),
      optionId,
      fulfillerId,
      startSec: timeSlot.start_sec,
      start: startDateTime.toJSDate(),
      end: endDateTime.toJSDate(),
      quantity: timeSlot?.quantity,
    };
  };

export const adaptTimeSlotPerDateList = (
  availabilityResult: AvailabilityResult,
  shop: BookingShop,
): TimeSlotPerDateInterface[] => {
  const timeZone = shop.timeZone;
  const fulfillerId = availabilityResult?.fulfillerId;
  const optionId = availabilityResult?.optionId;

  const timeSlots: SlotInterface[] =
    availabilityResult?.timeSlots?.map(
      (timeSlot: OneFFAvailabilitiesByServiceItemOptionTimeSlot) => {
        return adaptOneFFAvailabilitiesByServiceItemOptionTimeSlotToSlotInterface(
          timeSlot,
          fulfillerId,
          optionId,
          timeZone,
        );
      },
    ) ?? [];

  let dates = timeSlots?.reduce(
    (acc: TimeSlotPerDateInterface[], timeSlot: SlotInterface) => {
      const _acc = acc;
      const existingItemIndex = acc?.findIndex(({ date }) =>
        isSameDate(date, timeSlot.start),
      );

      if (existingItemIndex < 0) {
        _acc.push({
          date: timeSlot.start,
          slots: [timeSlot],
        });
        return _acc;
      }

      _acc?.[existingItemIndex]?.slots.push(timeSlot);
      return _acc;
    },
    [],
  );

  dates = dates?.map((item: TimeSlotPerDateInterface) => {
    return {
      ...item,
      slots: item?.slots
        // sort timeslots chronologically
        ?.toSorted((currentSlot, nextSlot) =>
          currentSlot?.start > nextSlot?.start ? 1 : -1,
        ),
    };
  });

  return (
    dates
      // only display days when timeslots are available.
      ?.filter((day) => day.slots.length > 0)
      // sort days chronologically
      .toSorted((current, next) => (current?.date > next?.date ? 1 : -1))
  );
};
